'use client';

import { useMemo, type ReactNode } from 'react';
import { Button, Tooltip } from '@heroui/react';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa6';
import { useStepNavigation } from '../StepNavigationContext';
import { useScheduler } from '../SchedulerContext';
import { useStepAccess } from '../utils/useStepAccess';
import { checkEscapeHatch } from '../utils/checkEscapeHatch';

type SchedulerHeaderProps = {
  allServices?: unknown;
  contactRoles?: unknown;
};

export default function SchedulerHeader({}: SchedulerHeaderProps) {
  const { activeStep, nextStep, previousStep } = useStepNavigation();
  const {
    companyStyles,
    bookingStatus,
    setBookingStatus,
    selectedServiceIds,
    services,
    containingServiceAreaArray,
    address,
    scheduleSettings,
    isSendingQuote,
  } = useScheduler();

  const navLocked = bookingStatus !== 'idle' || isSendingQuote;

  const escapeHatchWouldApply = useMemo(
    () =>
      checkEscapeHatch({
        selectedServiceIds,
        services,
        containingServiceAreaArray,
        address,
        maxSqftOnline: scheduleSettings.maxSqftOnline,
      }) !== null,
    [
      selectedServiceIds,
      services,
      containingServiceAreaArray,
      address,
      scheduleSettings.maxSqftOnline,
    ]
  );

  // What still stops the customer leaving this step. `useStepAccess` is the
  // authority rather than `useStepValidation` because leaving a step is not only
  // about data: the Property step also has to have been walked to its last
  // internal page, or the customer skips the addon upsell that lives before it.
  const { getAdvanceBlockers } = useStepAccess();
  const advanceBlockers = getAdvanceBlockers(activeStep);
  const canAdvance = advanceBlockers.length === 0;

  // Handle next step with validation
  const handleNextStep = () => {
    if (canAdvance) {
      nextStep();
    }
  };

  const backButton =
    activeStep > 1 ? (
      <Button
        size='sm'
        variant='flat'
        startContent={<FaArrowLeft />}
        onPress={previousStep}
        isDisabled={navLocked}
        className={companyStyles.secondaryButtonClassName}
        style={companyStyles.secondaryButtonStyles}
      >
        Back
      </Button>
    ) : null;

  const headerActions = (primary: ReactNode) => (
    <div className='flex flex-shrink-0 items-center gap-2'>
      {backButton}
      {primary}
    </div>
  );

  // Outstanding-work list used by both the Next and Schedule Now tooltips.
  const advanceBlockersTooltip = (trigger: ReactNode) => (
    <Tooltip
      content={
        <div className='max-w-xs'>
          <p className='mb-1 font-semibold'>Before You Continue:</p>
          <ul className='list-disc pl-4 text-xs'>
            {advanceBlockers.map((blocker, index) => (
              <li key={index}>{blocker}</li>
            ))}
          </ul>
        </div>
      }
      placement='bottom'
      delay={300}
    >
      <span>{trigger}</span>
    </Tooltip>
  );

  // Show "Schedule Now" button on summary step (step 5) - disabled when the
  // escape hatch applies or when anything earlier in the funnel is unfinished.
  // The blockers are the step 5 rollup here, which re-checks services, property
  // (including required info), slot and contact.
  if (activeStep === 5) {
    const scheduleButton = (
      <Button
        size='sm'
        onPress={() => setBookingStatus('loading')}
        isLoading={bookingStatus === 'loading'}
        isDisabled={navLocked || escapeHatchWouldApply || !canAdvance}
        className={`font-semibold ${companyStyles.secondaryButtonClassName}`}
        style={companyStyles.secondaryButtonStyles}
      >
        Schedule Now
      </Button>
    );

    return headerActions(
      canAdvance ? scheduleButton : advanceBlockersTooltip(scheduleButton)
    );
  }

  // Show "Next" button for all other steps with validation
  const nextButton = (
    <Button
      size='sm'
      endContent={<FaArrowRight />}
      onPress={handleNextStep}
      isDisabled={!canAdvance}
      className={companyStyles.secondaryButtonClassName}
      style={companyStyles.secondaryButtonStyles}
    >
      Next
    </Button>
  );

  // Show tooltip naming what is outstanding if the step cannot be left yet
  if (!canAdvance) {
    return headerActions(advanceBlockersTooltip(nextButton));
  }

  return headerActions(nextButton);
}
